import React from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import i18n, {setOnLanguageChange} from './i18n'
import { toast } from '@/hooks/use-toast'

import { reportJavaScriptError } from '@/api/error-reporter'
import { createMutationErrorHandler, createQueryErrorReporter } from '@/lib/error-handlers'
import { createAppQueryDefaults } from '@/lib/query-client-defaults'
import { handleUnauthorized } from '@/lib/handle-unauthorized'
import App from './App'
import './index.css'
import './lib/monaco-loader'

// WHY: tenant CSS must load AFTER index.css so its .tenant rules win the
// cascade against the bundle's :root defaults at equal specificity (Tailwind
// strips the @layer base wrapper, leaving everything unlayered — source
// order decides). Injecting the link here, after the bundle CSS import,
// puts /brand/theme.css last in document.styleSheets (issue 712).
{
    const link = document.createElement('link')
    /* eslint-disable i18next/no-literal-string -- DOM API attribute name + static URL, not user-visible */
    link.rel = 'stylesheet'
    link.href = '/brand/theme.css'
    /* eslint-enable i18next/no-literal-string */
    document.head.appendChild(link)
}

const isMock = import.meta.env.VITE_MOCK_SERVICES === 'true'

 
window.onerror = (_event, source, _lineno, _colno, error) => {
  const message = error?.message ?? String(_event)
  reportJavaScriptError(message, source ?? undefined, error?.stack)
}

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const message = event.reason?.message ?? String(event.reason)
  reportJavaScriptError(message, undefined, event.reason?.stack)
}
 

function isImpactBody(errorDetail?: string): boolean {
    if (!errorDetail) return false
    try {
        const parsed = JSON.parse(errorDetail) as Record<string, unknown>
        return parsed.migrationRequired !== undefined
    } catch (e) {
        void e // not JSON — treat as non-impact error
        return false
    }
}

const queryReporter = createQueryErrorReporter()
const mutationHandler = createMutationErrorHandler({
  isImpactBody,
  serverUnavailableMessage: () => i18n.t('common.serverUnavailableMessage'),
  toast: ({description, errorLogUrl}) => toast({
    variant: 'destructive',
    title: i18n.t('common.errorTitle'),
    description,
    duration: 10_000,
    errorLogUrl,
  }),
})

const queryClient = new QueryClient({
  defaultOptions: createAppQueryDefaults(),
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleUnauthorized(error)
      void queryReporter(error, query.queryKey)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      handleUnauthorized(error)
      void mutationHandler(error, mutation.options.mutationKey, mutation.options.meta)
    },
  }),
})

setOnLanguageChange(() => queryClient.invalidateQueries())

const rootElement = document.getElementById('root')!

if (isMock) {
    // WHY: lazy-import the mock entry point + store so the ~7 MB generated
    // mock testdata bundle and the fetch interceptor it installs at module
    // load time stay out of the real-mode dependency graph. Under suite
    // load the extra modules made the real-mode goto sequence slow enough
    // that in-flight requests aborted before React could mount.
    void Promise.all([
        import('./mock/MockApp'),
        import('./services/mock/default-store'),
    ]).then(([{default: MockApp}, {createDefaultMockStore}]) => {
        const store = createDefaultMockStore()
        window.__mockStore = store
        window.__queryClient = queryClient
        createRoot(rootElement).render(
            <React.StrictMode>
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <MockApp store={store} />
                    </BrowserRouter>
                </QueryClientProvider>
            </React.StrictMode>,
        )
    })
} else {
    createRoot(rootElement).render(
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </QueryClientProvider>
        </React.StrictMode>,
    )
}
